<svelte:options runes={true} />
<script lang="ts">import Button from "../components/Button.svelte";
import ArrowRight from "../components/icon/ArrowRight.svelte";
import Input from "../components/Input.svelte";
import SvelteMarkdown from "../compat/SvelteMarkdown.svelte";
import FriendlyCaptcha from "../components/FriendlyCaptcha.svelte";
import { defaults } from "./data/selfAssessmentDefaultOptions";
import { preserveCursor } from "../util/handlers";
import { sendDYEvents } from "./utils/dy";
import { getCookie } from "../util/cookie";
import { onMount, untrack } from 'svelte';
import { replaceHiddenInputValues } from '../../src/modules/replaceHiddenInputValues';
onMount(() => {
    replaceHiddenInputValues();
});
const nameFieldName = "name";
let { qnas = [], onSuccess = (_event) => { }, options = defaults } = $props();
let lastname = $state("");
let firstname = $state("");
let form = $state(null);
let disabled = $state(false);
let solution = $state("");
let values = $state(untrack(() => ({
    [nameFieldName]: "",
    [options.emailFieldName]: "",
    [options.phoneNumberFieldName]: "",
    marketingConsent: false,
})));
let validation = $state(untrack(() => ({
    [nameFieldName]: true,
    [options.emailFieldName]: true,
    [options.phoneNumberFieldName]: true,
    marketingConsent: true,
})));
let preValidationValues = $state(untrack(() => ({
    [nameFieldName]: "",
    [options.emailFieldName]: "",
    [options.phoneNumberFieldName]: "",
})));
$effect(() => {
    const parts = values[nameFieldName].split(" ");
    firstname = parts.shift() || "";
    lastname = parts.join(" ");
});
function getUtmTagsFromUrlOrCookie() {
    let utmParams = decodeURIComponent(window.location.search)
        .substr(1)
        .split("&")
        .filter((p) => p.indexOf("utm_") > -1);
    if (utmParams.length > 0) {
        return utmParams.map((param) => param.split("="));
    }
    const utmCookie = getCookie("sec_utm");
    if (utmCookie.length > 0) {
        return utmCookie
            .split("&")
            .map((param) => param.split("="));
    }
    return [];
}
async function postForm(token) {
    if (options.skipPost) {
        return Promise.resolve({ success: true, message: "From skipPost" });
    }
    const formData = new FormData(form || undefined);
    // Remove spaces from the phone number before appending it to the formData
    const phoneNumber = values[options.phoneNumberFieldName];
    const sanitizedPhoneNumber = typeof phoneNumber == "string" ? phoneNumber.replace(/\s+/g, '') : "";
    formData.set(options.phoneNumberFieldName, sanitizedPhoneNumber);
    formData.set("token", token);
    formData.set("contentReference", options.id || "");
    formData.set("sent_from_form", "self-assessment");
    formData.set("reasonofContact", options.reasonOfContact);
    formData.set("marketo_campaign", options.marketoCampaign);
    const utmParams = getUtmTagsFromUrlOrCookie();
    utmParams.forEach(([key, value]) => {
        formData.set(key, value);
    });
    // this whole exercise because multiple values may share one 'Form Field' in a 'Form Capture'
    const postValues = {};
    pushPostValues(postValues);
    // Update form data with the grouped values
    Object.keys(postValues).forEach((key) => {
        formData.set(key, postValues[key].join("\n"));
    });
    // prepare for x-www-form-urlencoded POST
    const entries = [...formData.entries()].map((e) => encodeURIComponent(e[0]) + "=" + encodeURIComponent(e[1].toString()));
    const res = await fetch("/api/clickdimensions/send", {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: entries.join("&"),
    });
    return res.json();
}
function pushPostValues(postValues) {
    if (!!window.DY) {
        pushValueToKey(options.geoLocationFieldName, window.DY.geoCity, postValues);
    }
    if (!!window.ga) {
        const clientId = window.ga.getAll()[0].get("clientId");
        if (clientId) {
            pushValueToKey(options.gaCidFieldName, clientId, postValues);
        }
    }
    pushValueToKey(options.sentFromPageFieldName, location.href, postValues);
    if (qnas.length > 0) {
        pushValueToKey(options.questionAnswersFieldName, qnas.join("\n"), postValues);
    }
}
function pushValueToKey(key, value, postValues) {
    let values = postValues[key] || [];
    values.push(value);
    postValues[key] = values;
}
async function handleSubmit(e) {
    if (disabled)
        return false;
    disabled = true;
    validate();
    if (Object.values(validation).some((v) => !v)) {
        disabled = false;
        return false;
    }
    let token = options.siteKey ? solution : "";
    const { success, message } = await postForm(token);
    if (success) {
        onSuccess(e);
        sendDYEvents(options.category);
    }
    else {
        disabled = false;
        console.error({ message });
    }
}
function validate() {
    // Reset all validation states to true before validating
    validation = {
        [nameFieldName]: true,
        [options.emailFieldName]: true,
        [options.phoneNumberFieldName]: true,
        marketingConsent: true,
    };
    const name = values[nameFieldName];
    const email = values[options.emailFieldName];
    const phone = values[options.phoneNumberFieldName];
    const marketingConsent = values.marketingConsent;
    if (name.length < 3) {
        setInvalidField(name, nameFieldName, options.invalidNameText);
    }
    if (typeof email == "string" && !emailValid(email)) {
        setInvalidField(email, options.emailFieldName, options.invalidEmailText);
    }
    if (typeof phone == "string" && phone.length < 5) {
        setInvalidField(phone, options.phoneNumberFieldName, options.invalidPhoneText);
    }
    // Validate marketing consent if it's required
    if (options.marketingConsent && !marketingConsent) {
        setInvalidField("", "marketingConsent", options.invalidMarketingConsent);
    }
}
function emailValid(email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
}
const handleInputFocus = (e) => {
    const { name } = e.target;
    if (validation[name])
        return;
    values = Object.assign(Object.assign({}, values), { [name]: preValidationValues[name] });
    validation = Object.assign(Object.assign({}, validation), { [name]: true });
};
function setInvalidField(value, fieldName, errorText) {
    preValidationValues[fieldName] = value;
    // If the field is marketingConsent, keep it as true/false (don't overwrite with the error message)
    if (fieldName !== "marketingConsent") {
        values = Object.assign(Object.assign({}, values), { [fieldName]: errorText });
    }
    else {
        // For marketingConsent, store only a boolean value (true/false)
        values = Object.assign(Object.assign({}, values), { [fieldName]: false });
    }
    validation = Object.assign(Object.assign({}, validation), { [fieldName]: false });
}
</script>

<form
	novalidate
	bind:this={form}
	method="POST"
	onsubmit={(e: SubmitEvent) => { e.preventDefault(); handleSubmit(e); }}
>
	<Input
		type="hidden"
		name={options.firstNameFieldName}
		bind:value={firstname}
	/>
	<Input type="hidden" name={options.lastNameFieldName} bind:value={lastname} />
	<div class="svelte-click-dimensions-form-container">
		<div class="svelte-click-dimensions-form">
			<div class="formControl">
				<Input
					bind:value={values[nameFieldName]}
					name={nameFieldName}
					error={!validation[nameFieldName]}
					placeholder={`${options.nameLabel}*`}
					required={true}
					onfocus={preserveCursor(handleInputFocus)}
				/>
			</div>
			<div class="flexClear-md"></div>
			<div class="formControl">
				<Input
					bind:value={values[options.phoneNumberFieldName]}
					name={options.phoneNumberFieldName}
					error={!validation[options.phoneNumberFieldName]}
					placeholder={`${options.phoneNumberLabel}*`}
					required={true}
					onfocus={preserveCursor(handleInputFocus)}
				/>
			</div>
			<div class="flexClear-md"></div>
			<div class="formControl">
				<Input
					bind:value={values[options.emailFieldName]}
					name={options.emailFieldName}
					error={!validation[options.emailFieldName]}
					placeholder={`${options.emailLabel}*`}
					required={true}
					onfocus={preserveCursor(handleInputFocus)}
				/>
			</div>
			<div class="flexClear-md"></div>

			<!-- Hidden input for source campaign -->
			<Input name="source_campaign" type="hidden" value="SOURCECAMPAIGN" />

			<div class="formControl">
				<Button
					disabled={disabled || (!solution && !!options.siteKey)}
					type="primary"
					text={options.submitText}><ArrowRight /></Button
				>
			</div>
				<div class="flexClear-lg"></div>
						{#if options.marketingConsent}
						<div class="formControl marketingConsentControl">
							<div class="marketingConsentInner">
								<label for="marketingConsent" class="checkboxLabel">
									<Input
										type="checkbox"
										name="marketingConsent"
										bind:value={values.marketingConsent}
										error={validation.marketingConsent === false}
									/>
									{#if options.marketingConsent.length > 0}
									<SvelteMarkdown source={options.marketingConsent} />
									{/if}
								</label>
								{#if validation.marketingConsent === false}
								<div class="marketingConsentError">{options.invalidMarketingConsent}</div>
								{/if}
							</div>
						</div>
						{/if}
						<div class="flexClear-lg"></div>
				{#if options.siteKey}
				<div class="formControl">
					<FriendlyCaptcha bind:solution={solution} siteKey={options.siteKey} language={options.language} />
				</div>
				{/if}
			</div>
		<div class="formInformation">
			<span class="requiredFields">* {options.requiredFieldsText}</span>
			{#if options.legalText.length > 0}
			<SvelteMarkdown source={options.legalText} />
			{/if}
		</div>
	</div>
</form>

<style lang="scss">.formInformation {
  text-align: left;
}
.formInformation .requiredFields {
  text-align: left;
  display: inline-block;
  margin-top: 0;
  margin-bottom: 1rem;
  width: 100%;
}

.svelte-click-dimensions-form {
  display: flex;
  flex-direction: row;
  justify-content: center;
  flex-wrap: wrap;
}
.svelte-click-dimensions-form .formControl {
  margin: 0 1rem 1rem 0;
  display: flex;
  align-items: center;
}
@media screen and (max-width: 980px) {
  .svelte-click-dimensions-form .flexClear-md {
    flex-basis: 100%;
    height: 0;
  }
}

@media screen and (max-width: 980px) {
  .flexClear-md {
    flex-basis: 100%;
    height: 0;
  }
}
@media screen and (min-width: 980px) {
  .flexClear-lg {
    margin-top: var(--spacing__24);
    flex-basis: 100%;
    height: 0;
  }
}
.formControl.marketingConsentControl {
  flex-basis: 100%;
  display: flex;
  justify-content: center;
  margin: 0 0 1rem 0;
}
.formControl.marketingConsentControl .marketingConsentInner {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
.formControl.marketingConsentControl .checkboxLabel {
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  line-height: 1.4;
}
.formControl.marketingConsentControl .checkboxLabel:after {
  content: "*";
  color: #aa1834;
  margin-left: 4px;
  font-weight: bold;
}
.formControl.marketingConsentControl .checkboxLabel :global(input[type="checkbox"]) {
  width: 20px;
  height: 20px;
  min-width: 20px;
  min-height: 20px;
  flex-shrink: 0;
  margin: 0 10px 0 0;
  padding: 0;
  appearance: none;
  -moz-appearance: none;
  -webkit-appearance: none;
  background-color: transparent;
  border: 2px solid #ffffff;
  border-radius: 3px;
  cursor: pointer;
  position: relative;
}
.formControl.marketingConsentControl .checkboxLabel :global(input[type="checkbox"]):checked {
  background-color: #ffffff;
  border-color: #ffffff;
}
.formControl.marketingConsentControl .checkboxLabel :global(input[type="checkbox"]):checked:after {
  content: "";
  position: absolute;
  left: 5px;
  top: 1px;
  width: 6px;
  height: 11px;
  border: solid #0a1f2e;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.formControl.marketingConsentControl .checkboxLabel :global(input[type="checkbox"]):hover {
  border-color: #cccccc;
}
.formControl.marketingConsentControl .checkboxLabel :global(input[type="checkbox"]):focus, .formControl.marketingConsentControl .checkboxLabel :global(input[type="checkbox"]):focus-visible {
  outline: 2px solid #8d5fff;
  outline-offset: 2px;
}
.formControl.marketingConsentControl .marketingConsentError {
  color: #aa1834;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}</style>